import { css, cx } from '@repo/pandacss/styled-system/css';
import { Details } from '@repo/ui-v2/Details';
import { MdExpandMore } from 'react-icons/md';
import { renderStructuredContent } from '../../rendererContext';
import {
  labelToDomIdSegment,
  sanitizeOptionalInstanceDomIdSegment,
} from '../../utils/sanitizeDomIdSegment';
import type { AccordionItemProps } from './types';

const expandIconClass = css({
  fontSize: 'xl',
  opacity: '[0.54]',
});

export function AccordionItem({ props }: AccordionItemProps) {
  if (!props || !props.label) return null;
  const structuredContentChildIndex = props.structuredContentChildIndex ?? 0;

  const fromInstance = props.instanceId?.trim()
    ? sanitizeOptionalInstanceDomIdSegment(props.instanceId)
    : '';

  const panelContentId = fromInstance
    ? `accordion-content-${fromInstance}`
    : `accordion-content-${labelToDomIdSegment(props.label, 'accordion-item')}-${structuredContentChildIndex}`;

  return (
    <li
      className={css({
        '&:last-of-type details': {
          borderBottomWidth: 'thin',
          borderBottomStyle: 'solid',
          borderBottomColor: 'neutral.lighter',
        },
      })}
    >
      <Details
        variant="simple"
        detailsProps={{
          open: props.initialStateExpanded,
          className: css({
            borderTopWidth: 'thin',
            borderTopStyle: 'solid',
            borderTopColor: 'neutral.lighter',
            backgroundColor: '[inherit]',
          }),
        }}
        summaryComponentProps={{
          'aria-controls': panelContentId,
          className: css({
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            paddingInlineStart: '2',
            paddingInlineEnd: '2',
            paddingBlockStart: '3',
            paddingBlockEnd: '3',
            _hover: {
              outlineWidth: 'none',
              outlineStyle: 'solid',
              outlineColor: 'blue.light',
              backgroundColor: 'blue.lightest',
              md: {
                outlineWidth: 'medium',
              },
            },
          }),
        }}
        SummaryComponent={
          <>
            <span className={css({ textStyle: 'textBold' })}>{props.label}</span>
            <MdExpandMore className={cx(expandIconClass, 'expand-icon')} aria-hidden="true" />
          </>
        }
      >
        <div
          id={panelContentId}
          className={css({
            paddingTop: '4',
            paddingRight: '4',
            paddingBottom: '4',
            paddingLeft: '4',
          })}
        >
          {renderStructuredContent(props.children, props.rendererProps)}
        </div>
      </Details>
    </li>
  );
}
